import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckboxInput, Divider, RadioButtons, TextInput, Typography, } from '@guildeducationinc/recess';
import { RequestFunding } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/AboutPaymentRequest/RequestFunding';
import { DeadlineErrorMessages } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/DeadlineErrorMessages/DeadlineErrorMessages';
import { ProgramDetails } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/ProgramDetails/ProgramDetails';
import { ProgramContext } from '@src/contexts/ProgramContextProvider';
import { useMyBenefits } from '@src/hooks/useMyBenefits';
import { PaymentMethod, PaymentRequestStatus, RuleType, } from '@src/types';
import { getDeadlineErrorMessage, getTermDate, isWithinDeadline, } from '@src/utilities/paymentRequestHelper';
import { DatePicker, TrackableButton, useLocale } from 'pe-core-frontend';
import { useContext, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PreimbursementQuestions } from '../../PreimbursementQuestions/PreimbursementQuestions';
import styles from './AboutPaymentRequest.module.scss';
export const AboutPaymentRequest = ({ program, paymentRequest, onChange, requestErrors, estCompDate, setEstCompDate, eligibilityLock, errors, }) => {
    const { t, formatToLocalDate } = useLocale();
    // Clean paymentRequest data for useMyBenefits - convert empty strings to undefined
    const cleanedPaymentRequest = useMemo(() => {
        if (!paymentRequest)
            return undefined;
        return {
            ...paymentRequest,
            termStartDate: paymentRequest.termStartDate || undefined,
            termEndDate: paymentRequest.termEndDate || undefined,
        };
    }, [paymentRequest]);
    const { myBenefits } = useMyBenefits({ program, paymentRequest: cleanedPaymentRequest });
    // Get funding for current program
    const fundingPolicy = useMemo(() => {
        return (myBenefits?.fundings?.find(f => f.program?.id === program.id) || myBenefits?.fundings?.[0]); // fallback to first if no program match
    }, [myBenefits?.fundings, program.id]);
    const fundingCap = fundingPolicy?.cap || 0;
    const fundingLimit = fundingPolicy?.remaining || 0;
    const currency = fundingPolicy?.currency || 'USD';
    const handleValueChange = (field) => (value) => {
        onChange({ ...paymentRequest, [field]: value });
    };
    const handleDatesSameAsProgramChange = (datesSameAsProgram) => {
        onChange({
            ...paymentRequest,
            datesSameAsProgram,
            termStartDate: datesSameAsProgram ? program.startDate : paymentRequest.termStartDate,
            termEndDate: datesSameAsProgram ? program.estCompDate : paymentRequest.termEndDate,
        });
    };
    const handleRequiresExpensesChange = (value) => {
        onChange({
            ...paymentRequest,
            requiresExpenses: value,
        });
    };
    const termDatesReadonly = paymentRequest.status === PaymentRequestStatus.ACTIVE;
    const { getPolicy } = useContext(ProgramContext);
    const approvalChainPolicy = getPolicy(RuleType.APPROVAL_CHAIN, program);
    // Check if budget policy is enabled
    const budgetPolicy = getPolicy(RuleType.BUDGET, program);
    const hasBudgetPolicy = budgetPolicy && budgetPolicy.approvalMode;
    const approvalPolicyConfig = paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT
        ? approvalChainPolicy?.preimbursementApproval
        : approvalChainPolicy?.paymentRequestApproval;
    const deadline = approvalPolicyConfig?.enableDeadline
        ? approvalPolicyConfig?.deadline
        : undefined;
    const documentDeadline = approvalPolicyConfig?.documentDeadline || {};
    const startDate = getTermDate(paymentRequest, documentDeadline.minIntervalBasedOn, documentDeadline.minInterval);
    const endDate = getTermDate(paymentRequest, documentDeadline.maxIntervalBasedOn, documentDeadline.maxInterval);
    // Determine if we should show the "requires expenses" radio buttons
    const showRequiresExpenses = useMemo(() => !eligibilityLock.preimbursement &&
        eligibilityLock.canSubmitExpenses &&
        isWithinDeadline(paymentRequest, approvalChainPolicy) &&
        [PaymentRequestStatus.PENDING, PaymentRequestStatus.IN_REVIEW].includes(paymentRequest.status), [
        eligibilityLock.preimbursement,
        eligibilityLock.canSubmitExpenses,
        paymentRequest.termStartDate,
        paymentRequest.termEndDate,
        approvalChainPolicy,
    ]);
    useEffect(() => {
        const updates = {};
        if (!paymentRequest.paymentMethod) {
            updates.paymentMethod = eligibilityLock.preimbursement
                ? undefined
                : PaymentMethod.REIMBURSEMENT;
        }
        if (!showRequiresExpenses) {
            // Default requiresExpenses to True when status is not PENDING or IN_REVIEW
            const defaultRequiresExpenses = eligibilityLock.preimbursement ||
                ![PaymentRequestStatus.PENDING, PaymentRequestStatus.IN_REVIEW].includes(paymentRequest.status)
                ? true
                : eligibilityLock.expensesRequired;
            if (paymentRequest.requiresExpenses !== defaultRequiresExpenses) {
                updates.requiresExpenses = defaultRequiresExpenses;
            }
        }
        if (Object.keys(updates).length > 0) {
            onChange({
                ...paymentRequest,
                ...updates,
            });
        }
    }, [
        eligibilityLock.preimbursement,
        showRequiresExpenses,
        eligibilityLock.expensesRequired,
        paymentRequest,
    ]);
    let eligLockComeBackMessage = '';
    if (paymentRequest.status === PaymentRequestStatus.ACTIVE && !eligibilityLock.canSubmitExpenses) {
        const [message, params] = getDeadlineErrorMessage(formatToLocalDate, false, documentDeadline, startDate ?? null, endDate ?? null);
        eligLockComeBackMessage = t(message, {
            values: params,
        });
    }
    const requiresExpensesSection = (_jsxs(_Fragment, { children: [showRequiresExpenses && (_jsx(RadioButtons, { groupLabel: `${t('Have you already paid for these expenses?')} *`, name: 'requiresExpenses', onChange: e => handleRequiresExpensesChange(e.target.value === 'true'), selectedValue: String(paymentRequest.requiresExpenses ?? false), values: [
                    {
                        label: t('Yes, I have already paid'),
                        value: 'true',
                    },
                    {
                        label: t('No, I have not paid yet'),
                        value: 'false',
                    },
                ] })), (paymentRequest.requiresExpenses ?? false) === false && hasBudgetPolicy && (_jsx(RequestFunding, { paymentRequest: paymentRequest, onChange: onChange, fundingCap: fundingCap, fundingLimit: fundingLimit, currency: currency, clientId: paymentRequest.clientId, errors: requestErrors }))] }));
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.heading, children: [_jsx(Typography, { variant: 'title2', children: eligibilityLock.locked
                            ? termDatesReadonly
                                ? t('Confirm your completion date')
                                : t('Tell us about your {progLevel} term or billing period', {
                                    values: { progLevel: program.programLevelName },
                                })
                            : t('A little more about your {progLevel}', {
                                values: { progLevel: program.programLevelName },
                            }) }), _jsx(Typography, { variant: 'label2', children: t('* Required') })] }), _jsx(ProgramDetails, { program: program, showStartDate: !termDatesReadonly, showCompletionDate: !termDatesReadonly, termDates: termDatesReadonly
                    ? [paymentRequest.termStartDate, paymentRequest.termEndDate]
                    : undefined }), !paymentRequest.id && // Only show preimbursement questions for new payment requests
                eligibilityLock.preimbursement && (_jsxs(_Fragment, { children: [_jsx("hr", { className: 'my-2' }), _jsx(PreimbursementQuestions, { program: program, paymentRequest: paymentRequest, onChange: onChange, requestErrors: requestErrors })] })), !paymentRequest.datesSameAsProgram && (_jsxs("div", { children: [_jsx(Typography, { variant: 'title3', children: "Confirm program completion date" }), _jsx(Typography, { variant: 'body1', className: 'mb-3', children: "Please verify your program completion date is correct" }), _jsx(DatePicker, { label: 'Completion date *', value: estCompDate || '', onChange: setEstCompDate, helpText: t('Date you expect to complete your program') })] })), !termDatesReadonly && (_jsxs(_Fragment, { children: [!paymentRequest.datesSameAsProgram && _jsx(Divider, { opacity: '1' }), _jsxs("div", { className: 'flex flex-col', children: [_jsx(Typography, { variant: 'title3', children: t('Term details') }), _jsx(Typography, { variant: 'body1', children: eligibilityLock.locked
                                    ? t((deadline?.minInterval?.days ?? 0) === 0
                                        ? 'Term eligibility lock description_0_days'
                                        : 'Term eligibility lock description', {
                                        values: {
                                            link: (text) => (_jsx(TrackableButton, { btnType: 'link', component: Link, enText: 'Employer policy', tag: 'submit_confirmation', to: '/reimbursement-policy', children: text })),
                                            days: deadline?.minInterval?.days ?? 0,
                                        },
                                    })
                                    : t('Term description') }), _jsxs("div", { className: styles.termFields, children: [_jsx(TextInput, { label: `${t('Term Name')} *`, helpText: t('Example: Spring 2026'), value: paymentRequest.termName || '', onChange: e => handleValueChange('termName')(e.target.value), errors: requestErrors.termName }), _jsx(CheckboxInput, { label: t('The initial start date and completion date is the same as the term start and end date for my current term.'), checked: paymentRequest.datesSameAsProgram ?? false, onChange: e => handleDatesSameAsProgramChange(e.target.checked) }), !paymentRequest.datesSameAsProgram && (_jsxs(_Fragment, { children: [_jsx(DatePicker, { label: `${t('Start Date')} *`, value: paymentRequest.termStartDate || '', onChange: handleValueChange('termStartDate'), errors: requestErrors.termStartDate }), _jsx(DatePicker, { label: `${t('End Date')} *`, value: paymentRequest.termEndDate || '', onChange: handleValueChange('termEndDate'), errors: requestErrors.termEndDate })] })), _jsx(DeadlineErrorMessages, { messages: errors })] })] })] })), _jsx("div", { className: 'mt-4', children: requiresExpensesSection }), paymentRequest.status === PaymentRequestStatus.ACTIVE &&
                !eligibilityLock.canSubmitExpenses && (_jsxs("div", { className: styles.lockedWarning, children: [_jsx(Typography, { variant: 'body1', weight: '700', children: t('Come back soon! You can submit expenses for this term as early as X and no later than Y', {
                            values: {
                                startDate,
                                endDate,
                            },
                        }) }), _jsx(Typography, { variant: 'label2', weight: '400', children: eligLockComeBackMessage })] }))] }));
};
